import FacebookLogo from 'icons/facebook.svg';
import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Box, Flex } from 'components/box';
import { Text } from 'components/typography';
import { closeDrawer } from '../../../../redux/global/actions';
import { logError } from '../../../../util/error-logger';
import { useSocialLogin } from 'hooks/customer';
import { useDispatch } from 'react-redux';
import Loading from 'components/loading';
import Script from 'next/script';
import { AuthenticationContext } from 'types/types';
import config from '../../../../util/load-config';
import ErrorBoundary from 'components/_global/error-boundary';
import { useAddGlobalStatus, useHasGlobalStatus } from 'hooks/app';
import {
  isFacebookValid,
  isValidStatusResponse,
  WindowFacebook,
} from './types';
import { LoginMethodEnum } from 'types/third-party/gtm';

declare global {
  interface Window {
    FB?: WindowFacebook;
  }
}

const SOCIAL_LOGIN_PROVIDER = 'FACEBOOK';

const FACEBOOK_STATUS_LOADED = 'facebook-loaded';
const FACEBOOK_STATUS_INITIALIZED = 'facebook-initialized';

interface FacebookAuthProps {
  context: AuthenticationContext;
  setError: (error?: string) => void;
}

/**
 * NOTE: we could choose to keep a reference to the window.FB object once loaded
 * but I think it's safer to fetch it from scratch whenever we need it, in case it's been replaced.
 */
const FacebookAuth = ({ context, setError }: FacebookAuthProps) => {
  const dispatch = useDispatch();

  /**
   * NOTE: global status is used because the Script.onLoad event only triggers once per "page load".
   * so without it, the second time you render this button in one "page load", it will never load.
   */
  const addGlobalStatus = useAddGlobalStatus();
  const isFacebookLoaded = useHasGlobalStatus(FACEBOOK_STATUS_LOADED);
  const isFacebookInitialized = useHasGlobalStatus(FACEBOOK_STATUS_INITIALIZED);

  const [isDisabled, setIsDisabled] = useState(false);

  const [socialLogin, { loading, error, completed }] = useSocialLogin(
    LoginMethodEnum.FACEBOOK
  );

  const login = useCallback(() => {
    if (!isFacebookInitialized) return;

    try {
      if (
        typeof window !== 'undefined' &&
        window.FB &&
        isFacebookValid(window.FB)
      ) {
        window.FB.login(
          data => {
            try {
              if (!isValidStatusResponse(data)) {
                logError(
                  new Error(
                    `Invalid data for Facebook login: ${JSON.stringify(data)}`
                  ),
                  'facebookAuthenticated'
                );

                throw new Error('Invalid data for Facebook login.');
              }

              const accessToken = data.authResponse?.accessToken;
              if (accessToken) {
                socialLogin({ provider: SOCIAL_LOGIN_PROVIDER, accessToken });
              }
            } catch (e) {
              setError(
                'Facebook login failed. Please refresh the page and try again.'
              );
            }
          },
          { scope: 'email', return_scopes: true }
        );
      }
    } catch (e) {
      setError('Facebook login failed. Please refresh the page and try again.');
    }
  }, [isFacebookInitialized, setError, socialLogin]);

  /**
   * Initialize FB sdk.
   */
  useEffect(() => {
    if (!isFacebookLoaded || isFacebookInitialized) return;

    try {
      if (
        typeof window !== 'undefined' &&
        window.FB &&
        isFacebookValid(window.FB)
      ) {
        window.FB.init({
          appId: config.social.facebookAppId,
          version: config.social.facebookAppVersion,
          cookie: true,
          xfbml: true,
        });

        addGlobalStatus(FACEBOOK_STATUS_INITIALIZED);
      }
    } catch (e) {
      setError(
        'Failed to initialize Facebook. Please refresh the page and try again.'
      );
      setIsDisabled(true);
    }
  }, [isFacebookLoaded, isFacebookInitialized, addGlobalStatus, setError]);

  useEffect(() => setError(error), [error, setError]);

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [completed, dispatch]);

  return (
    <>
      <Script
        id="facebook-sdk"
        strategy="lazyOnload"
        src="https://connect.facebook.net/en_US/sdk.js"
        onLoad={() => addGlobalStatus(FACEBOOK_STATUS_LOADED)}
      />

      <Loading isLoading={!isDisabled && (!isFacebookInitialized || loading)}>
        <button
          disabled={!isFacebookInitialized || isDisabled}
          className="btn btn-facebook"
          onClick={login}
          type="button"
          css={css`
            padding: 2px 3px 2px 3px;
            width: 100%;
            &:disabled {
              cursor: progress;
            }
          `}
        >
          <Flex
            width="100%"
            alignItems="center"
            backgroundColor={
              isFacebookInitialized && !isDisabled
                ? 'facebookLight'
                : 'facebookDark'
            }
            borderRadius={1}
            pr="0.9rem"
          >
            <Box pt="8.5px" pb="4px" px="0.7rem">
              <FacebookLogo />
            </Box>

            <Text
              color="white"
              fontSize={[1, 2]}
              fontWeight={500}
              fontFamily="header"
              m="0 auto"
              letterSpacing="0.2px"
            >
              {!loading && (
                <>
                  {context === AuthenticationContext.LogIn
                    ? 'Log in with Facebook'
                    : 'Sign up with Facebook'}
                </>
              )}

              {loading && 'Signing in...'}
            </Text>
          </Flex>
        </button>
      </Loading>
    </>
  );
};

const Wrapper = (props: FacebookAuthProps) => (
  <ErrorBoundary>
    <FacebookAuth {...props} />
  </ErrorBoundary>
);

export default Wrapper;
